import React from 'react';
import { clsx } from 'clsx';
import DOMPurify from 'dompurify';

interface SlateNode {
  type?: string;
  children?: SlateNode[];
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  url?: string;
  level?: number;
}

interface RichTextRendererProps {
  content: string;
  htmlContent?: string; // Pre-processed HTML content from backend
  className?: string;
  maxLength?: number;
  showExpandButton?: boolean;
  onExpand?: () => void;
  isExpanded?: boolean;
}

/**
 * RichTextRenderer Component
 * 
 * Renders Slate.js JSON format and HTML content safely.
 * Supports both rich text JSON and plain HTML content.
 */
export const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  content,
  htmlContent,
  className,
  maxLength,
  showExpandButton = true,
  onExpand,
  isExpanded = false
}) => {
  /**
   * Parse Slate.js JSON format to HTML
   */
  const parseSlateJSON = (nodes: SlateNode[]): string => {
    if (!Array.isArray(nodes)) return '';

    return nodes.map(node => {
      if (node.text !== undefined) {
        // Text node
        let text = escapeHtml(node.text);
        
        if (node.bold) text = `<strong>${text}</strong>`;
        if (node.italic) text = `<em>${text}</em>`;
        if (node.underline) text = `<u>${text}</u>`;
        if (node.strikethrough) text = `<s>${text}</s>`;
        if (node.code) text = `<code>${text}</code>`;
        
        return text;
      }

      // Element node
      if (node.children) {
        const children = parseSlateJSON(node.children);
        
        switch (node.type) {
          case 'paragraph':
            return `<p>${children}</p>`;
          case 'heading-one':
            return `<h1>${children}</h1>`;
          case 'heading-two':
            return `<h2>${children}</h2>`;
          case 'heading-three':
            return `<h3>${children}</h3>`;
          case 'heading-four':
            return `<h4>${children}</h4>`;
          case 'heading-five':
            return `<h5>${children}</h5>`;
          case 'heading-six':
            return `<h6>${children}</h6>`;
          case 'block-quote':
            return `<blockquote>${children}</blockquote>`;
          case 'numbered-list':
            return `<ol>${children}</ol>`;
          case 'bulleted-list':
            return `<ul>${children}</ul>`;
          case 'list-item':
            return `<li>${children}</li>`;
          case 'link':
            return `<a href="${escapeHtml(node.url || '')}" target="_blank" rel="noopener noreferrer">${children}</a>`;
          case 'image':
            return `<img src="${escapeHtml(node.url || '')}" alt="${children}" style="max-width: 100%; height: auto;" />`;
          case 'code-block':
            return `<pre><code>${children}</code></pre>`;
          case 'table':
            return `<table class="border-collapse border border-gray-300 w-full"><tbody>${children}</tbody></table>`;
          case 'table-row':
            return `<tr>${children}</tr>`;
          case 'table-cell':
            return `<td class="border border-gray-300 p-2">${children}</td>`;
          default:
            return children;
        }
      }

      return '';
    }).join('');
  };

  /**
   * Escape HTML entities to prevent XSS
   */
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  };

  /**
   * Sanitize HTML content by removing dangerous elements
   */
  const sanitizeHTML = (html: string): string => {
    return DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'style'],
      FORBID_ATTR: ['style'],
    });
  };

  /**
   * Better JSON detection for Slate.js format
   */
  const isSlateJSON = (content: string): boolean => {
    try {
      const parsed = JSON.parse(content);
      return Array.isArray(parsed) && parsed.every(node => 
        typeof node === 'object' && node !== null && (
          'type' in node || 'text' in node || 'children' in node
        )
      );
    } catch {
      return false;
    }
  };

  /**
   * Process content - detect format and convert to HTML
   */
  const processContent = (rawContent: string, htmlContent?: string): string => {
    // Handle empty content
    if (!rawContent || rawContent.trim() === '') {
      return '';
    }

    // If we have pre-processed HTML content from backend and it's not JSON, use it
    if (htmlContent && htmlContent.trim() !== '' && !isSlateJSON(htmlContent)) {
      return sanitizeHTML(htmlContent);
    }

    // Better JSON detection for Slate.js format
    if (isSlateJSON(rawContent)) {
      try {
        const slateNodes = JSON.parse(rawContent);
        const htmlContent = parseSlateJSON(slateNodes);
        return sanitizeHTML(htmlContent);
      } catch (error) {
        console.error('Failed to parse Slate.js JSON content:', error);
        // Fall through to treat as plain text
      }
    }

    // Check if htmlContent is Slate JSON and use it
    if (htmlContent && isSlateJSON(htmlContent)) {
      try {
        const slateNodes = JSON.parse(htmlContent);
        const processedHtml = parseSlateJSON(slateNodes);
        return sanitizeHTML(processedHtml);
      } catch (error) {
        console.error('Failed to parse Slate.js JSON htmlContent:', error);
        // Fall through to rawContent
      }
    }

    // If it contains HTML tags, sanitize and return
    if (rawContent.includes('<') && rawContent.includes('>')) {
      return sanitizeHTML(rawContent);
    }

    // Plain text - convert line breaks to <br> tags
    return escapeHtml(rawContent).replace(/\n/g, '<br>');
  };

  // Process the content - prefer htmlContent over content
  const processedHTML = processContent(content, htmlContent);
  
  /**
   * HTML-aware truncation that preserves tag structure
   */
  const truncateHTML = (html: string, maxLength: number): { truncated: string; wasTruncated: boolean } => {
    // Remove HTML tags to count actual text length
    const textContent = html.replace(/<[^>]*>/g, '');
    
    if (textContent.length <= maxLength) {
      return { truncated: html, wasTruncated: false };
    }
    
    // Simple truncation approach: truncate text and add ellipsis
    let currentLength = 0;
    let truncated = '';
    let insideTag = false;
    
    for (let i = 0; i < html.length; i++) {
      const char = html[i];
      
      if (char === '<') {
        insideTag = true;
      } else if (char === '>') {
        insideTag = false;
      } else if (!insideTag) {
        currentLength++;
        if (currentLength > maxLength) {
          truncated += '...';
          break;
        }
      }
      
      truncated += char;
    }
    
    return { truncated, wasTruncated: true };
  };

  // Handle content truncation
  let displayHTML = processedHTML;
  let isTruncated = false;

  // Re-enable truncation with proper HTML-aware logic
  if (maxLength && !isExpanded) {
    const result = truncateHTML(processedHTML, maxLength);
    displayHTML = result.truncated;
    isTruncated = result.wasTruncated;
  }

  return (
    <div className={clsx('rich-text-content', className)}>
      <div
        className={clsx(
          'text-gray-700 break-words leading-relaxed',
          'rich-text-prose',
          // Rich text specific styles
          '[&_p]:mb-2 [&_p:last-child]:mb-0',
          '[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mb-2 [&_h1]:mt-2',
          '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:mt-2',
          '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:mt-2',
          '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mb-2 [&_h4]:mt-2',
          '[&_h5]:text-sm [&_h5]:font-semibold [&_h5]:mb-2 [&_h5]:mt-2',
          '[&_h6]:text-sm [&_h6]:font-semibold [&_h6]:mb-2 [&_h6]:mt-2',
          '[&_ul]:mb-2 [&_ul]:pl-6 [&_ul]:list-disc',
          '[&_ol]:mb-2 [&_ol]:pl-6 [&_ol]:list-decimal',
          '[&_li]:mb-1',
          '[&_blockquote]:mb-2 [&_blockquote]:p-2 [&_blockquote]:bg-gray-50 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:italic',
          '[&_pre]:mb-2 [&_pre]:p-2 [&_pre]:bg-gray-100 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:font-mono [&_pre]:text-sm',
          '[&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:font-mono [&_code]:text-sm',
          '[&_a]:text-blue-600 [&_a]:underline hover:[&_a]:text-blue-800',
          '[&_img]:mb-2 [&_img]:rounded [&_img]:max-w-full [&_img]:h-auto',
          '[&_table]:mb-2 [&_table]:w-full [&_table]:border-collapse',
          '[&_td]:border [&_td]:border-gray-300 [&_td]:p-2',
          '[&_th]:border [&_th]:border-gray-300 [&_th]:p-2 [&_th]:bg-gray-50 [&_th]:font-medium'
        )}
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(displayHTML) }}
      />
      
      {isTruncated && showExpandButton && onExpand && (
        <button
          onClick={onExpand}
          className="ml-2 text-primary-600 hover:text-primary-700 font-medium focus:outline-none focus:underline text-sm"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};